<template>
    <div class="@container/inbox flex flex-col overflow-y-auto scroll-thin md:-mt-6 md:-mr-6 md:-mb-3">
        <div v-if="loadingCategories" class="flex items-center justify-center p-12">
            <page-loading></page-loading>
        </div>

        <div v-else-if="!hasCategories" class="p-6 pt-12 md:pl-0">
            <vx-box color="info" class="flex flex-col px-8 py-20 gap-6 items-center justify-center h-80">
                <div class="flex flex-col gap-1 items-center">
                    <vx-badge size="xs" color="violet">
                        <font-awesome-icon :icon="faSparkles"></font-awesome-icon>
                        AI
                    </vx-badge>
                    <span class="font-bold text-base text-slate-700">Intelligent Inbox</span>
                </div>
                <p class="text-sm text-slate-500 text-center max-w-xs">
                    Intelligent Inbox hasn't been configured yet. Contact your corporate team to set up categories.
                </p>
            </vx-box>
        </div>

        <div v-else class="flex flex-col gap-6 p-6 md:pl-0 md:pb-3">
            <!-- Header -->
            <motion.div
                :initial="fadeUpVariants.hidden"
                :animate="fadeUpVariants.visible"
                :transition="sectionTransition(0)"
                class="flex justify-center gap-2 pt-6 pb-4 items-center text-center"
            >
                <h1 class="text-3xl font-bold">Intelligent Inbox</h1>
                <vx-badge size="xs" color="violet">
                    <font-awesome-icon :icon="faSparkles"></font-awesome-icon>
                    AI
                </vx-badge>
            </motion.div>

            <!-- Daily Brief -->
            <motion.div
                :initial="fadeUpVariants.hidden"
                :animate="fadeUpVariants.visible"
                :transition="sectionTransition(1)"
                class="mx-auto w-full max-w-2xl"
            >
                <dashboard-daily-brief />
            </motion.div>

            <!-- Top Priority Conversations -->
            <motion.div
                :initial="fadeUpVariants.hidden"
                :animate="fadeUpVariants.visible"
                :transition="sectionTransition(3)"
            >
                <h2 class="text-base flex font-bold text-slate-800 mb-3 justify-between items-center">
                    <span>Top Priority Conversations</span>
                </h2>
                <div class="grid grid-cols-1 gap-3 @lg/inbox:grid-cols-3">
                    <template v-if="loadingThreads">
                        <div class="rounded bg-slate-300 animate-pulse h-56"></div>
                        <div class="rounded bg-slate-300 animate-pulse h-56"></div>
                        <div class="rounded bg-slate-300 animate-pulse h-56"></div>
                    </template>

                    <vx-box
                        v-else-if="priorityThreads.length === 0"
                        color="info"
                        class="col-span-full flex items-center justify-center px-8 py-12"
                    >
                        <p class="text-sm text-slate-500">No priority conversations at this time.</p>
                    </vx-box>

                    <motion.div v-else initial="hidden" animate="visible" :transition="cardStagger" class="contents">
                        <motion.div
                            v-for="thread in priorityThreads"
                            :key="`${thread.group_id ?? ''}-${thread.contact_id}`"
                            :variants="fadeUpVariants"
                            :transition="quickTransition"
                        >
                            <dashboard-priority-card
                                :thread="thread"
                                @snoozed="onThreadSnoozed(thread)"
                                @unsnoozed="onThreadUnsnoozed(thread)"
                            />
                        </motion.div>
                    </motion.div>
                </div>
            </motion.div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { faSparkles } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxBox, VxBadge } from '@voxie/frontend-components';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { motion } from 'motion-v';
import PageLoading from '~/components/general/PageLoading.vue';
import { useIntelligentInbox } from '../useIntelligentInbox';
import { fadeUpVariants, quickTransition, cardStagger, sectionTransition } from './dashboardMotionVariants';
import { threadsClient } from '~/services';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { useToasted } from '~/composables/useToasted';
import { catchRequestError } from '~/utils/request';
import type { Thread } from '~/services/threads/threadsClient.types';
import { isSameThread } from '~/components/message-hub/messageHubUtil';
import DashboardDailyBrief from './DashboardDailyBrief.vue';
import DashboardPriorityCard from './DashboardPriorityCard.vue';
import Bus from '~/globals/eventBus';
import dayjs, { toUTCDateTimeFormat } from '~/utils/dayjs';

const PRIORITY_WINDOW_DAYS = 7;

const { getContext } = useCurrentGroup();
const toasted = useToasted();

const { fetchCategories, loadingCategories, categories } = useIntelligentInbox();

const hasCategories = computed(() => (categories.value ?? []).length > 0);

const updatePriorityThread = (thread: Thread): void => {
    priorityThreads.value = priorityThreads.value.map((item) => {
        if (!isSameThread(item, thread)) {
            return item;
        }

        return {
            ...item,
            ...thread,
        };
    });
};

const onThreadSnoozed = (thread: Thread): void => {
    updatePriorityThread({ ...thread, thread_section: 'snoozed' });
};

const onThreadUnsnoozed = (thread: Thread): void => {
    updatePriorityThread({ ...thread, thread_section: 'pending' });
};

const priorityThreads = ref<Thread[]>([]);
const loadingThreads = ref(false);

fetchCategories().then(() => {
    if ((categories.value ?? []).length > 0) {
        fetchThreads();
    }
});

const handleThreadUpdated = ({ thread }: { thread: Thread }): void => {
    updatePriorityThread(thread);
};

const fetchThreads = async (): Promise<void> => {
    if (loadingThreads.value) {
        return;
    }
    loadingThreads.value = true;

    try {
        const startDate = dayjs().subtract(PRIORITY_WINDOW_DAYS, 'day');

        const response = await threadsClient.getPriority(getContext(), {
            start_date: toUTCDateTimeFormat(startDate),
            limit: 3,
        });

        priorityThreads.value = response.data.data;
    } catch (error) {
        toasted.error(catchRequestError(error));
        priorityThreads.value = [];
    } finally {
        loadingThreads.value = false;
    }
};

onMounted(() => {
    Bus.$on('threadUpdated', handleThreadUpdated);
});

onUnmounted(() => {
    Bus.$off('threadUpdated', handleThreadUpdated);
});
</script>
